/**
 * Sources List Tab Component
 *
 * Container component with tabs for Domains, Pages, and Phones
 */

import { useState, forwardRef, useImperativeHandle, useRef } from 'react'
import { Tabs } from 'antd'
import {
  GlobalOutlined,
  LinkOutlined,
  PhoneOutlined,
} from '@ant-design/icons'

import DomainsListTab from './DomainsListTab'
import PagesListTab from './PagesListTab'
import PhonesListTab from './PhonesListTab'

interface SourcesListTabProps {
  projectId: number
}

const SourcesListTab = forwardRef(({ projectId }: SourcesListTabProps, ref) => {
  const [activeTab, setActiveTab] = useState('domains')

  const domainsRef = useRef<any>(null)
  const pagesRef = useRef<any>(null)
  const phonesRef = useRef<any>(null)

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    triggerSync: async () => {
      if (domainsRef.current?.triggerSync) {
        await domainsRef.current.triggerSync()
      }
    },
    triggerGenerateConfig: async () => {
      if (domainsRef.current?.triggerGenerateConfig) {
        await domainsRef.current.triggerGenerateConfig()
      }
    },
    refresh: () => {
      if (activeTab === 'domains' && domainsRef.current?.refresh) {
        domainsRef.current.refresh()
      } else if (activeTab === 'pages' && pagesRef.current?.refresh) {
        pagesRef.current.refresh()
      } else if (activeTab === 'phones' && phonesRef.current?.refresh) {
        phonesRef.current.refresh()
      }
    },
  }))

  const items = [
    {
      key: 'domains',
      label: (
        <span>
          <GlobalOutlined style={{ marginRight: 6 }} />
          Домены
        </span>
      ),
      children: <DomainsListTab ref={domainsRef} projectId={projectId} />,
    },
    {
      key: 'pages',
      label: (
        <span>
          <LinkOutlined style={{ marginRight: 6 }} />
          Страницы
        </span>
      ),
      children: <PagesListTab ref={pagesRef} projectId={projectId} />,
    },
    {
      key: 'phones',
      label: (
        <span>
          <PhoneOutlined style={{ marginRight: 6 }} />
          Телефоны
        </span>
      ),
      children: <PhonesListTab ref={phonesRef} projectId={projectId} />,
    },
  ]

  return (
    <Tabs
      activeKey={activeTab}
      onChange={setActiveTab}
      items={items}
      type="card"
      style={{ marginTop: -8 }}
    />
  )
})

SourcesListTab.displayName = 'SourcesListTab'

export default SourcesListTab
